(() => {
  if (window.__shoevFlagshipRuntime) return;
  window.__shoevFlagshipRuntime = true;
  const MODEL_VIEWER_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.3.1/model-viewer.min.js';

  function ensureModelViewerRuntime() {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (window.__shoevModelViewerRuntimePromise) return window.__shoevModelViewerRuntimePromise;
    window.__shoevModelViewerRuntimePromise = new Promise((resolve, reject) => {
      let script = [...document.scripts].find((item) => item.src === MODEL_VIEWER_SRC);
      if (!script) {
        script = document.createElement('script');
        script.type = 'module';
        script.src = MODEL_VIEWER_SRC;
        document.head.append(script);
      }
      const timer = window.setTimeout(() => {
        window.__shoevModelViewerRuntimePromise = null;
        reject(new Error('Model viewer runtime timed out'));
      }, 8000);
      customElements.whenDefined('model-viewer').then(() => {
        window.clearTimeout(timer);
        resolve();
      });
    });
    return window.__shoevModelViewerRuntimePromise;
  }

  function init(root) {
    if (!root?.isConnected || root.dataset.flagshipReady) return;
    root.dataset.flagshipReady = 'true';
    const cleanups = [];
    const on = (element, event, callback, options) => {
      element?.addEventListener(event, callback, options);
      cleanups.push(() => element?.removeEventListener(event, callback, options));
    };
    root.__shoevCleanup = () => {
      cleanups.splice(0).forEach((cleanup) => cleanup());
      delete root.dataset.flagshipReady;
    };

    const tabs = [...root.querySelectorAll('[data-media-target]')];
    const panels = [...root.querySelectorAll('[data-media-key]')];
    let mediaSelectionRequest = 0;
    let productJsonPromise;

    const getProductJson = () => {
      if (productJsonPromise) return productJsonPromise;
      try {
        const productUrl = new URL(root.dataset.url, window.location.origin);
        productUrl.pathname = `${productUrl.pathname.replace(/\.js$/, '').replace(/\/$/, '')}.js`;
        productUrl.search = '';
        productUrl.hash = '';
        productJsonPromise = fetch(productUrl.toString(), { headers: { Accept: 'application/json' } })
          .then((response) => {
            if (!response.ok) throw new Error('Product JSON request failed');
            return response.json();
          })
          .catch(() => null);
      } catch {
        productJsonPromise = Promise.resolve(null);
      }
      return productJsonPromise;
    };

    const getInlineVariant = (tab) => {
      if (!tab.dataset.variantId) return null;
      try {
        const options = JSON.parse(tab.dataset.variantOptions || '[]');
        if (!Array.isArray(options) || !options.length) return null;
        return { id: tab.dataset.variantId, options };
      } catch {
        return null;
      }
    };

    const resolveVariantForMedia = async (tab) => {
      const inlineVariant = getInlineVariant(tab);
      if (inlineVariant) return inlineVariant;

      const mediaId = tab.dataset.mediaTarget?.match(/^media-(\d+)$/)?.[1];
      if (!mediaId) return null;

      const productJson = await getProductJson();
      const variant = productJson?.variants?.find((item) => String(item.featured_media?.id) === String(mediaId));
      if (!variant?.id || !Array.isArray(variant.options)) return null;

      tab.dataset.variantId = String(variant.id);
      tab.dataset.variantOptions = JSON.stringify(variant.options);
      return variant;
    };

    const selectVariantForMedia = async (tab, requestId) => {
      const variant = await resolveVariantForMedia(tab);
      if (!variant || requestId !== mediaSelectionRequest || !tab.isConnected || !root.isConnected) return false;

      const variantSelects = root.querySelector('variant-selects');
      if (!variantSelects) return false;

      const optionValues = variant.options;
      if (!Array.isArray(optionValues) || !optionValues.length) return false;

      const selectedVariant = variantSelects.querySelector('[data-selected-variant]');
      try {
        if (String(JSON.parse(selectedVariant?.textContent || '{}').id) === String(variant.id)) return true;
      } catch {
        // Continue with control values when Dawn's selected-variant payload is unavailable.
      }

      const optionGroups = [...variantSelects.querySelectorAll('fieldset, select')].filter(
        (group) => group.tagName === 'FIELDSET' || !group.closest('fieldset')
      );
      if (optionGroups.length < optionValues.length) return false;

      const resolvedControls = optionValues.map((value, index) => {
        const group = optionGroups[index];
        if (group.tagName === 'SELECT') {
          const option = [...group.options].find((item) => item.value === value);
          return option ? { control: group, option } : null;
        }
        const input = [...group.querySelectorAll('input[type="radio"]')].find((item) => item.value === value);
        return input ? { control: input } : null;
      });
      if (resolvedControls.some((item) => !item)) return false;

      resolvedControls.forEach(({ control, option }) => {
        if (control.tagName === 'SELECT') {
          [...control.options].forEach((item) => item.removeAttribute('selected'));
          option.selected = true;
          option.setAttribute('selected', 'selected');
          control.value = option.value;
        } else {
          control.checked = true;
        }
      });

      const eventControl =
        resolvedControls.find(({ control }) => control.closest('.shoev-flagship__option--color'))?.control ||
        resolvedControls[resolvedControls.length - 1].control;
      eventControl.dispatchEvent(
        new CustomEvent('change', {
          bubbles: true,
          detail: { shoevPreserveScroll: true },
        })
      );
      return true;
    };

    const activateMedia = (key, focus = false) => {
      const activeTab = tabs.find((tab) => tab.dataset.mediaTarget === key);
      if (!activeTab) return false;
      if (activeTab.classList.contains('is-active')) {
        if (focus) activeTab.focus();
        return true;
      }
      window.pauseAllMedia?.();
      tabs.forEach((tab) => {
        const active = tab === activeTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel.dataset.mediaKey === key;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      if (focus) activeTab.focus();
      return true;
    };

    tabs.forEach((tab, index) => {
      on(tab, 'click', () => {
        activateMedia(tab.dataset.mediaTarget);
        mediaSelectionRequest += 1;
        void selectVariantForMedia(tab, mediaSelectionRequest);
      });
      on(tab, 'keydown', (event) => {
        let nextIndex;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (nextIndex === undefined) return;
        event.preventDefault();
        activateMedia(tabs[nextIndex].dataset.mediaTarget, true);
      });
    });

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS?.variantChange
    ) {
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data?.sectionId) !== String(root.dataset.section)) return;
        mediaSelectionRequest += 1;
        const mediaId = data?.variant?.featured_media?.id;
        if (mediaId) activateMedia(`media-${mediaId}`);
      });
      cleanups.push(() => unsubscribe?.());
    }

    root.querySelectorAll('.shoev-flagship__option--color input').forEach((input) => {
      on(input, 'change', () => {
        const output = input.closest('fieldset')?.querySelector('[data-selected-value]');
        if (output) output.textContent = input.value;
      });
    });

    const wrap = root.querySelector('[data-model-wrap]');
    const viewer = wrap?.querySelector('model-viewer');
    const loader = wrap?.querySelector('[data-model-loader]');
    if (viewer && loader) {
      const fill = loader.querySelector('[data-loader-fill]');
      const text = loader.querySelector('[data-loader-text]');
      const bar = loader.querySelector('[role=progressbar]');
      const progress = (event) => {
        if (!event.detail) return;
        const value = Math.round(event.detail.totalProgress * 100);
        fill.style.width = `${value}%`;
        text.textContent = `${root.dataset.loaderProgressLabel || 'Loading 3D model —'} ${value}%`;
        bar.setAttribute('aria-valuenow', value);
      };
      let loaderHideTimer;
      const hideLoader = () => {
        loader.hidden = true;
        loader.setAttribute('aria-hidden', 'true');
      };
      const done = () => {
        if (loader.classList.contains('is-complete')) return;
        fill.style.width = '100%';
        bar.setAttribute('aria-valuenow', '100');
        viewer.setAttribute('aria-busy', 'false');
        loader.classList.add('is-complete');
        loaderHideTimer = window.setTimeout(hideLoader, 400);
      };
      const fail = () => {
        window.clearTimeout(loaderHideTimer);
        loader.classList.remove('is-complete');
        loader.classList.add('is-error');
        loader.hidden = false;
        loader.removeAttribute('aria-hidden');
        viewer.setAttribute('aria-busy', 'false');
        text.textContent = root.dataset.loaderError || 'The 3D view could not be loaded.';
      };
      on(viewer, 'progress', progress);
      on(viewer, 'load', done);
      on(viewer, 'model-visibility', (event) => event.detail?.visible && done());
      on(viewer, 'error', fail);
      ensureModelViewerRuntime().catch(fail);
      cleanups.push(() => window.clearTimeout(loaderHideTimer));
    }
    on(wrap, 'wheel', (event) => event.preventDefault(), { passive: false });
    on(wrap, 'touchmove', (event) => event.preventDefault(), { passive: false, capture: true });
  }

  function boot(scope = document) {
    if (scope.matches?.('[data-shoev-flagship]')) init(scope);
    scope.querySelectorAll?.('[data-shoev-flagship]').forEach(init);
  }
  boot();
  document.addEventListener('product-info:loaded', (event) => boot(event.target));
  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
    if (typeof ProductModel !== 'undefined' && typeof ProductModel.loadShopifyXR === 'function') {
      ProductModel.loadShopifyXR();
    }
  });
  document.addEventListener('shopify:section:unload', (event) =>
    event.target.querySelectorAll?.('[data-shoev-flagship]').forEach((root) => root.__shoevCleanup?.())
  );
})();
